import { useMemo } from 'react';
import { useFinanceDebts } from '@/hooks/queries';
import { debtRows, debtsUnreadyMessage } from '@/lib/loansDebtsRead';

// ADR-031 (GitHub #92) — the "settles debt" picker shared by every expenditure write surface
// (manual add, bank-classify, edit). Selecting a debt tags the expenditure with its DebtId so
// paying a debt is the ordinary "money out" gesture pointed at the debt it settles. Orthogonal
// to the expenditure's category — a debt payment keeps its real Schedule B purpose code.
//
// Renders NOTHING when there are no active (non-voided) debts to point at — a non-federal
// campaign has none, and a federal one with no debts shouldn't see an empty control. Debts still
// carrying a balance lead; paid-off ones are listed (a late/correcting payment is possible) but
// de-emphasized. A debt SETTLED or FORGIVEN below face value is left out entirely: the creditor
// released the committee from it, so there is nothing left to pay and the backend refuses the
// tag. The backend also guards against overpaying a debt.

const money = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

interface SettlesDebtPickerProps {
  /** Currently-selected debt id, or null for "not a debt payment". */
  value: string | null;
  onChange: (debtId: string | null) => void;
  /** Optional id for the <select> so a <label htmlFor> can point at it. */
  id?: string;
  /** Render just the bare <select> (no "Settles debt (optional)" label wrapper) — for use
   *  inside a surface that supplies its own label (e.g. the ledger detail grid). */
  hideLabel?: boolean;
}

export function SettlesDebtPicker({ value, onChange, id, hideLabel = false }: SettlesDebtPickerProps) {
  const debtsRead = useFinanceDebts(undefined);
  const debts = debtRows(debtsRead);
  const unready = debtsUnreadyMessage(debtsRead);

  const active = useMemo(
    () =>
      debts
        .filter((d) => d.voidedAtUtc === null && d.settlementKind === null)
        // Still-owed first, then paid off — stable within each bucket.
        .sort((a, b) => Number(b.outstandingBalance !== 0) - Number(a.outstandingBalance !== 0)),
    [debts],
  );

  if (unready) {
    return (
      <p role="alert" className="text-xs text-red-600">
        {unready}
      </p>
    );
  }
  if (active.length === 0) return null;

  const select = (
    <select
      id={id}
      aria-label="Settles debt"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      className="w-full rounded-md border px-3 py-2 text-sm"
    >
      <option value="">— Not a debt payment —</option>
      {active.map((d) => (
        <option key={d.id} value={d.id}>
          {d.contactName ?? 'Creditor'} — {d.purpose}
          {d.outstandingBalance !== 0 ? ` (${money(d.outstandingBalance)} owed)` : ' (paid off)'}
        </option>
      ))}
    </select>
  );

  if (hideLabel) return select;

  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium">Settles debt (optional)</span>
      {select}
    </label>
  );
}
